import { useGetMediaLibFilesMutation } from "@api/media-library";
import {
  fetchAndSortMediaFilesImagesByResolution,
  MEDIA_LIB_FILES_QUERY_KEY,
  MediaLibCategoryEnum,
} from "@editor/modules/media-lib";
import debounce from "lodash.debounce";
import { useCallback, useEffect, useRef, useState } from "react";
import { track } from "@/features/analytics";
import type { IMediaFile } from "@/shared/types";

const PER_PAGE = 30;

interface IProps {
  amtOfFiles: number;
  category: MediaLibCategoryEnum;
  setFiles: (data: { files: IMediaFile[]; extend?: boolean }) => void;
  selectedFolderId: string | null;
  willLoadFiles: boolean;
  setWillLoadFiles: (value: boolean) => void;
  searchQuery: string;
}

interface IFetchProps {
  replace?: boolean;
  signal?: AbortSignal;
  nextPage?: boolean;
  query?: string;
}

export const useMediaLibFetchFiles = ({
  amtOfFiles,
  category,
  setFiles,
  selectedFolderId,
  willLoadFiles,
  setWillLoadFiles,
  searchQuery,
}: IProps) => {
  const isInit = useRef(true);
  const [isReplacingFiles, setIsReplacingFiles] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [isError, setIsError] = useState(false);
  const [reloadKey, setReloadKey] = useState({});
  const isMoreToFetch = useRef(true);
  const lastSystemQuery = useRef("");
  const latestPage = useRef(0);

  const [fetchFiles] = useGetMediaLibFilesMutation({
    fixedCacheKey: MEDIA_LIB_FILES_QUERY_KEY,
  });

  const fetchPageOfFiles = async ({
    query = searchQuery || lastSystemQuery.current,
    signal,
    replace,
    nextPage,
  }: IFetchProps) => {
    setIsError(false);
    setIsFetchingMore(true);
    try {
      if (replace) {
        setIsReplacingFiles(true);
        isMoreToFetch.current = true;
        latestPage.current = 0;
      } else if (nextPage) {
        setIsFetchingMore(true);
        latestPage.current++;
      }

      let folderId: string | null = "";
      const includeSubfolders = false;
      let typeGroup: string | undefined;
      if (
        query.length === 0 &&
        [MediaLibCategoryEnum.Images, MediaLibCategoryEnum.Videos].includes(
          category,
        )
      ) {
        typeGroup =
          category === MediaLibCategoryEnum.Images ? "image" : "video";
      }

      if (!query) {
        if (selectedFolderId) folderId = selectedFolderId;
        else if (
          [
            MediaLibCategoryEnum.BrandAssets,
            MediaLibCategoryEnum.Website,
            MediaLibCategoryEnum.Import,
          ].includes(category)
        ) {
          folderId = category;
          // includeSubfolders = true;
        } else {
          folderId = null;
          // includeSubfolders = true;
        }
      }

      const isImportOrWebsite = [
        MediaLibCategoryEnum.Website,
        MediaLibCategoryEnum.Import,
      ].includes(category);

      const data = await fetchFiles({
        perPage: isImportOrWebsite ? 125 : PER_PAGE,
        typeGroup,
        search: query || undefined,
        page: latestPage.current,
        signal: signal,
        archived: category === MediaLibCategoryEnum.Archive,
        folderId: folderId,
        includeSubfolders,
      }).unwrap();

      if (data.length === 0) {
        isMoreToFetch.current = false;
        if (nextPage) {
          latestPage.current--;
        }
      } else isMoreToFetch.current = true;

      if (isImportOrWebsite) {
        setFiles({
          files: await fetchAndSortMediaFilesImagesByResolution(data),
          extend: nextPage,
        });
      } else {
        setFiles({
          files: data,
          extend: nextPage,
        });
      }
    } catch (e) {
      if (!signal?.aborted) {
        track("error fetching media library files", {
          error: e,
          query: query,
        });
        setIsError(true);
      }
    } finally {
      if (!signal?.aborted) {
        setIsReplacingFiles(false);
        setWillLoadFiles(false);
        setIsFetchingMore(false);
      }
    }
  };

  const replaceFilesData = async (controller: AbortController) => {
    setIsError(false);
    setIsReplacingFiles(true);

    lastSystemQuery.current = searchQuery;
    await fetchPageOfFiles({
      query: searchQuery,
      replace: true,
      signal: controller.signal,
    });
    if (!controller.signal.aborted) {
      setIsReplacingFiles(false);
      setWillLoadFiles(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    if (isInit.current) {
      isInit.current = false;
      if (amtOfFiles === 0) {
        replaceFilesData(controller);
      } else {
        setIsReplacingFiles(false);
        setWillLoadFiles(false);
      }
      return () => {
        controller.abort();
      };
    }

    replaceFilesData(controller);

    return () => {
      controller.abort();
    };
  }, [searchQuery, category, reloadKey, selectedFolderId]);

  const canLoadMoreFiles = !isFetchingMore && !isReplacingFiles;

  const fetchMoreFiles = useCallback(
    debounce(() => {
      if (
        !canLoadMoreFiles ||
        amtOfFiles < PER_PAGE / 2 ||
        !isMoreToFetch.current
      ) {
        return;
      }

      fetchPageOfFiles({ nextPage: true });
    }, 100),
    [canLoadMoreFiles, amtOfFiles, fetchPageOfFiles],
  );

  return {
    isError,
    reloadData: () => setReloadKey({}),
    fetchMoreFiles,
    shouldShowSkeleton: isReplacingFiles || willLoadFiles,
    canLoadMoreFiles,
  };
};
